import type { QueryResult } from '@apollo/client';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import type { MessageSearchConstraints } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  ContextNodeFragment,
  ContextUserFragment,
  AuthUserFragment
} from '@aurora/shared-generated/types/graphql-types';
import type { EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import type { ParsedUrlQuery } from '@aurora/shared-utils/helpers/urls/NextRoutes/Route';
import type {
  MessageSearchQuery,
  MessageSearchQueryVariables
} from '../../../../types/graphql-types';
import { getSearchFilterParams } from '../../SearchHelper';
import messageSearchQuery from '../../../messages/MessageListForSearch/MessageSearch.query.graphql';

/**
 * This hook is used to fetch counts for the search dropdown menu item
 * @param query the search query params
 * @returns QueryResult of type Message Count Query
 *
 * @author Akash Goenka
 */
export default function useSearchFilterDropDownMenuItemCountHelper(
  query: ParsedUrlQuery,
  routeParam: EndUserQueryParams,
  filterItem: string,
  authUser: AuthUserFragment,
  shouldSkip: boolean,
  contextNode?: ContextNodeFragment,
  author?: ContextUserFragment
): QueryResult<MessageSearchQuery, MessageSearchQueryVariables> {
  query[routeParam] = filterItem;
  const searchTerm = (query.q as string)?.trim() ?? '';
  const messageCountConstraints: MessageSearchConstraints = getSearchFilterParams(
    query,
    authUser,
    author,
    contextNode
  );
  const messageCountVariables: MessageSearchQueryVariables = {
    constraints: {
      ...messageCountConstraints
    },
    searchTerm
  };
  return useQueryWithTracing<MessageSearchQuery, MessageSearchQueryVariables>(
    module,
    messageSearchQuery,
    {
      variables: messageCountVariables,
      skip: shouldSkip
    }
  );
}
